import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Button,
  Row,
  Col,
  Badge,
  Alert,
  Input,
  InputGroup,
  InputGroupText,
} from 'reactstrap';
import DataTable from '../../components/DataTable';
import ConfirmModal from '../../components/ConfirmModal';
import Loading from '../../components/Loading';
import SnowFallComponent from 'components/SnowFallComponent';
import { ventasService } from '../../services/ventasService';
import { sucursalesService } from '../../services/sucursalesService';
import { empresasService } from '../../services/empresasService';
import { useAuth } from '../../contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import VentaForm from './VentaForm';
import VentaDetalle from './VentaDetalle';

function Ventas() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [ventas, setVentas] = useState([]);
  const [empresa, setEmpresa] = useState(null);
  const [sucursales, setSucursales] = useState([]);
  const [sucursalSeleccionada, setSucursalSeleccionada] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingSucursales, setLoadingSucursales] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showDetalle, setShowDetalle] = useState(false);
  const [ventaSeleccionada, setVentaSeleccionada] = useState(null);
  const [formKey, setFormKey] = useState(0); // Key para forzar remontaje del formulario
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [ventaACancelar, setVentaACancelar] = useState(null);
  const [cancelando, setCancelando] = useState(false);
  const [motivoCancelacion, setMotivoCancelacion] = useState('');
  const [filtroCliente, setFiltroCliente] = useState('');
  const [filtroFechaDesde, setFiltroFechaDesde] = useState('');
  const [filtroFechaHasta, setFiltroFechaHasta] = useState('');
  const [filtroEstatus, setFiltroEstatus] = useState('');

  useEffect(() => {
    loadEmpresaYSucursales();
    
    // Verificar si hay parámetros de cotización en la URL
    const searchParams = new URLSearchParams(location.search);
    const cotizacionFolio = searchParams.get('cotizacionFolio');
    const cotizacionId = searchParams.get('cotizacionId');
    
    if (cotizacionFolio || cotizacionId) {
      setShowForm(true);
    }
  }, [location.search]);

  useEffect(() => {
    if (!loadingSucursales && sucursalSeleccionada) {
      loadVentas();
    }
  }, [sucursalSeleccionada, filtroCliente, filtroFechaDesde, filtroFechaHasta, filtroEstatus, loadingSucursales]);

  // Verificar si hay un ID en sessionStorage para abrir el detalle automáticamente
  useEffect(() => {
    const openDetailId = sessionStorage.getItem('openDetail_venta');
    if (openDetailId && !loading && sucursalSeleccionada) {
      // Cargar la venta específica desde la API para asegurar que esté disponible
      // independientemente del filtro de sucursal
      const loadVentaFromAPI = async () => {
        try {
          const response = await ventasService.getById(openDetailId);
          if (response.success && response.data) {
            sessionStorage.removeItem('openDetail_venta');
            handleView(response.data);
          } else {
            // Si no se encuentra en la API, intentar buscarla en la lista actual
            sessionStorage.removeItem('openDetail_venta');
            const venta = ventas.find(v => 
              (v.VentaID || v.ventaID)?.toString() === openDetailId
            );
            if (venta) {
              handleView(venta);
            }
          }
        } catch (error) {
          // Si hay error, intentar buscarla en la lista actual como fallback
          sessionStorage.removeItem('openDetail_venta');
          const venta = ventas.find(v => 
            (v.VentaID || v.ventaID)?.toString() === openDetailId
          );
          if (venta) {
            handleView(venta);
          }
        }
      };
      
      loadVentaFromAPI();
    }
  }, [loading, ventas, sucursalSeleccionada]);

  const loadEmpresaYSucursales = async () => {
    try {
      setLoadingSucursales(true);
      const empresasResponse = await empresasService.getAll();
      if (empresasResponse.success && empresasResponse.data) {
        const empresasData = Array.isArray(empresasResponse.data) ? empresasResponse.data : [];
        const empresaActiva = empresasData.find(emp => emp.Activo !== false && emp.activo !== false) || empresasData[0];
        if (empresaActiva) {
          setEmpresa(empresaActiva);
          const sucursalesResponse = await sucursalesService.getAll(empresaActiva.EmpresaID || empresaActiva.empresaID);
          if (sucursalesResponse.success && sucursalesResponse.data) {
            const sucursalesData = Array.isArray(sucursalesResponse.data) ? sucursalesResponse.data : [];
            const normalized = sucursalesData.map(suc => ({
              SucursalID: suc.SucursalID || suc.sucursalID,
              Nombre: suc.Nombre || suc.nombre || '',
            }));
            setSucursales(normalized);
            if (normalized.length > 0) {
              setSucursalSeleccionada(normalized[0].SucursalID.toString());
            }
          }
        }
      }
    } catch (err) {
      setError('Error al cargar datos de la empresa');
    } finally {
      setLoadingSucursales(false);
    }
  };

  const loadVentas = async () => {
    try {
      setLoading(true);
      setError('');
      const sucursalId = parseInt(sucursalSeleccionada);
      if (!sucursalId) return;

      const clienteId = filtroCliente ? parseInt(filtroCliente) : null;

      const response = await ventasService.getAll(
        sucursalId,
        clienteId,
        filtroFechaDesde || null,
        filtroFechaHasta || null,
        filtroEstatus || null
      );

      if (response.success && response.data) {
        const ventasData = Array.isArray(response.data) ? response.data : [];
        const normalized = ventasData.map(venta => ({
          VentaID: venta.VentaID || venta.ventaID,
          SucursalID: venta.SucursalID || venta.sucursalID,
          SucursalNombre: venta.SucursalNombre || venta.sucursalNombre || '',
          ClienteID: venta.ClienteID || venta.clienteID,
          ClienteNombre: venta.ClienteNombre || venta.clienteNombre,
          UsuarioID: venta.UsuarioID || venta.usuarioID,
          UsuarioNombre: venta.UsuarioNombre || venta.usuarioNombre || '',
          Folio: venta.Folio || venta.folio,
          Subtotal: venta.Subtotal !== undefined ? parseFloat(venta.Subtotal) : (venta.subtotal !== undefined ? parseFloat(venta.subtotal) : 0),
          DescuentoTotal: venta.DescuentoTotal !== undefined ? parseFloat(venta.DescuentoTotal) : (venta.descuentoTotal !== undefined ? parseFloat(venta.descuentoTotal) : 0),
          Impuestos: venta.Impuestos !== undefined ? parseFloat(venta.Impuestos) : (venta.impuestos !== undefined ? parseFloat(venta.impuestos) : 0),
          Total: venta.Total !== undefined ? parseFloat(venta.Total) : (venta.total !== undefined ? parseFloat(venta.total) : 0),
          Estatus: venta.Estatus || venta.estatus || '',
          FechaVenta: venta.FechaVenta || venta.fechaVenta,
          Observaciones: venta.Observaciones || venta.observaciones,
        }));
        setVentas(normalized);
      } else {
        setError(response.Message || response.message || 'Error al cargar ventas');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setVentaSeleccionada(null);
    setFormKey(prev => prev + 1); // Incrementar key para forzar remontaje del formulario
    // Limpiar parámetros de URL si existen
    if (location.search) {
      navigate(location.pathname, { replace: true });
    }
    setShowForm(true);
  };

  const handleView = async (venta) => {
    try {
      setLoading(true);
      const response = await ventasService.getDetalle(venta.VentaID || venta.ventaID);
      if (response.success && response.data) {
        setVentaSeleccionada(response.data);
        setShowDetalle(true);
      } else {
        setError(response.Message || response.message || 'Error al cargar el detalle de la venta');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = (venta) => {
    setVentaACancelar(venta);
    setShowCancelModal(true);
  };

  const confirmCancel = async () => {
    if (!motivoCancelacion.trim()) {
      setError('El motivo de cancelación es requerido');
      return;
    }

    try {
      setCancelando(true);
      const ventaId = ventaACancelar.VentaID || ventaACancelar.ventaID;
      const usuarioId = user?.UsuarioID || user?.usuarioID;

      const response = await ventasService.cancelar(ventaId, {
        UsuarioID: usuarioId,
        Motivo: motivoCancelacion.trim(),
      });

      if (response.success) {
        setSuccess('Venta cancelada exitosamente');
        setTimeout(() => setSuccess(''), 3000);
        setShowCancelModal(false);
        setVentaACancelar(null);
        setMotivoCancelacion('');
        loadVentas();
      } else {
        setError(response.Message || response.message || 'Error al cancelar la venta');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setCancelando(false);
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setSuccess('Venta creada exitosamente');
    setTimeout(() => setSuccess(''), 3000);
    loadVentas();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setFormKey(prev => prev + 1); // Incrementar key para resetear el formulario la próxima vez
    // Limpiar parámetros de URL si existen
    if (location.search) {
      navigate(location.pathname, { replace: true });
    }
  };

  const handleDetalleClose = () => {
    setShowDetalle(false);
    setVentaSeleccionada(null);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getEstatusBadge = (estatus) => {
    const estatusLower = (estatus || '').toLowerCase();
    if (estatusLower === 'completada') {
      return <Badge color="success">Completada</Badge>;
    } else if (estatusLower === 'cancelada') {
      return <Badge color="danger">Cancelada</Badge>;
    }
    return <Badge color="secondary">{estatus || 'Desconocido'}</Badge>;
  };

  const exportarVentasCSV = () => {
    if (!ventas || ventas.length === 0) {
      setError('No hay ventas para exportar');
      return;
    }

    const columnas = [
      { label: 'Folio', accessor: 'Folio', render: (v) => v || '-' },
      { label: 'Fecha', accessor: 'FechaVenta', render: (v) => formatDate(v) },
      { label: 'Cliente', accessor: 'ClienteNombre', render: (v) => v || 'Cliente General' },
      { label: 'Vendedor', accessor: 'UsuarioNombre' },
      { label: 'Subtotal', accessor: 'Subtotal', render: (v) => formatCurrency(v) },
      { label: 'Impuestos', accessor: 'Impuestos', render: (v) => formatCurrency(v) },
      { label: 'Total', accessor: 'Total', render: (v) => formatCurrency(v) },
      { label: 'Estatus', accessor: 'Estatus' },
    ];

    // Crear encabezados
    const headers = columnas.map(col => col.label).join(',');
    
    // Crear filas
    const rows = ventas.map(item => {
      return columnas.map(col => {
        const value = col.render ? col.render(item[col.accessor], item) : (item[col.accessor] || '');
        // Escapar comillas y envolver en comillas si contiene comas
        const stringValue = String(value).replace(/"/g, '""');
        return stringValue.includes(',') ? `"${stringValue}"` : stringValue;
      }).join(',');
    });

    // Combinar todo
    const csvContent = [headers, ...rows].join('\n');
    
    // Crear blob y descargar
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `ventas_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportarVentaDetalleCSV = async (venta) => {
    try {
      setLoading(true);
      const response = await ventasService.getById(venta.VentaID || venta.ventaID);
      if (!response.success || !response.data) {
        setError('Error al cargar el detalle de la venta');
        return;
      }

      const detalle = response.data;
      const folio = detalle.Folio || detalle.folio || `VENT-${detalle.VentaID || detalle.ventaID}`;
      
      // Crear contenido CSV
      let csvContent = 'VENTA\n';
      csvContent += `Folio,${folio}\n`;
      csvContent += `Fecha,${formatDate(detalle.FechaVenta || detalle.fechaVenta)}\n`;
      csvContent += `Cliente,${detalle.ClienteNombre || detalle.clienteNombre || 'Cliente General'}\n`;
      csvContent += `Vendedor,${detalle.UsuarioNombre || detalle.usuarioNombre}\n`;
      csvContent += `Sucursal,${detalle.SucursalNombre || detalle.sucursalNombre}\n`;
      csvContent += `Estatus,${detalle.Estatus || detalle.estatus}\n`;
      csvContent += `Subtotal,${formatCurrency(detalle.Subtotal || detalle.subtotal || 0)}\n`;
      csvContent += `Descuento Total,${formatCurrency(detalle.DescuentoTotal || detalle.descuentoTotal || 0)}\n`;
      csvContent += `Impuestos,${formatCurrency(detalle.Impuestos || detalle.impuestos || 0)}\n`;
      csvContent += `Total,${formatCurrency(detalle.Total || detalle.total || 0)}\n`;
      if (detalle.Observaciones || detalle.observaciones) {
        csvContent += `Observaciones,"${(detalle.Observaciones || detalle.observaciones).replace(/"/g, '""')}"\n`;
      }
      
      // Métodos de pago
      const metodosPago = detalle.MetodosPago || detalle.metodosPago || [];
      if (metodosPago.length > 0) {
        csvContent += '\n';
        csvContent += 'MÉTODOS DE PAGO\n';
        csvContent += 'Método de Pago,Monto\n';
        metodosPago.forEach(mp => {
          const metodoNombre = (mp.MetodoPagoNombre || mp.metodoPagoNombre || '').replace(/"/g, '""');
          const monto = mp.Monto !== undefined ? mp.Monto : (mp.monto || 0);
          csvContent += `"${metodoNombre}",${monto}\n`;
        });
      }
      
      csvContent += '\n';
      csvContent += 'DETALLES DE PRODUCTOS\n';
      csvContent += 'Producto,SKU,Cantidad,Precio Unitario,Descuento,Impuestos,Subtotal,Total\n';
      
      const detalles = detalle.Detalles || detalle.detalles || [];
      detalles.forEach(det => {
        const producto = (det.ProductoNombre || det.productoNombre || '').replace(/"/g, '""');
        const sku = (det.ProductoSKU || det.productoSKU || '').replace(/"/g, '""');
        const cantidad = det.Cantidad !== undefined ? det.Cantidad : (det.cantidad || 0);
        const precioUnit = det.PrecioUnitario !== undefined ? det.PrecioUnitario : (det.precioUnitario || 0);
        const descuento = det.Descuento !== undefined ? det.Descuento : (det.descuento || 0);
        const impuestos = det.Impuestos !== undefined ? det.Impuestos : (det.impuestos || 0);
        const subtotal = det.Subtotal !== undefined ? det.Subtotal : (det.subtotal || 0);
        const total = det.Total !== undefined ? det.Total : (det.total || 0);
        
        csvContent += `"${producto}","${sku}",${cantidad},${precioUnit},${descuento},${impuestos},${subtotal},${total}\n`;
      });

      // Crear blob y descargar
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `venta_${folio}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setError(err.message || 'Error al exportar la venta');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      key: 'folio',
      label: 'Folio',
      accessor: 'Folio',
      render: (value) => value || '-',
    },
    {
      key: 'fechaVenta',
      label: 'Fecha',
      accessor: 'FechaVenta',
      render: (value) => formatDate(value),
    },
    {
      key: 'cliente',
      label: 'Cliente',
      accessor: 'ClienteNombre',
      render: (value) => value || 'Cliente General',
    },
    {
      key: 'usuario',
      label: 'Vendedor',
      accessor: 'UsuarioNombre',
    },
    {
      key: 'subtotal',
      label: 'Subtotal',
      accessor: 'Subtotal',
      render: (value) => formatCurrency(value),
    },
    {
      key: 'impuestos',
      label: 'Impuestos',
      accessor: 'Impuestos',
      render: (value) => formatCurrency(value),
    },
    {
      key: 'total',
      label: 'Total',
      accessor: 'Total',
      render: (value) => formatCurrency(value),
    },
    {
      key: 'estatus',
      label: 'Estatus',
      accessor: 'Estatus',
      render: (value) => getEstatusBadge(value),
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (_, row) => {
        const estatusLower = (row.Estatus || '').toLowerCase();
        const puedeCancelar = estatusLower !== 'cancelada';
        return (
          <div>
            <Button
              color="info"
              size="sm"
              className="mr-2"
              onClick={(e) => {
                e.stopPropagation();
                handleView(row);
              }}
              title="Ver Detalle"
            >
              <i className="tim-icons icon-zoom-split" />
            </Button>
            <Button
              color="success"
              size="sm"
              className="mr-2"
              onClick={(e) => {
                e.stopPropagation();
                exportarVentaDetalleCSV(row);
              }}
              title="Exportar CSV"
            >
              <i className="tim-icons icon-cloud-download-93" />
            </Button>
            {puedeCancelar && (
              <Button
                color="danger"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancel(row);
                }}
                title="Cancelar Venta"
              >
                <i className="tim-icons icon-simple-remove" />
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  if (showForm) {
    const searchParams = new URLSearchParams(location.search);
    const cotizacionFolio = searchParams.get('cotizacionFolio');
    const cotizacionId = searchParams.get('cotizacionId');
    
    return (
      <VentaForm
        key={`${formKey}-${cotizacionFolio || ''}-${cotizacionId || ''}`} // Key incluye parámetros para forzar remontaje cuando cambian
        sucursalId={parseInt(sucursalSeleccionada)}
        usuarioId={user?.UsuarioID || user?.usuarioID}
        onSuccess={handleFormSuccess}
        onCancel={handleFormCancel}
        cotizacionFolio={cotizacionFolio}
        cotizacionId={cotizacionId}
      />
    );
  }

  if (showDetalle && ventaSeleccionada) {
    return (
      <VentaDetalle
        venta={ventaSeleccionada}
        onClose={handleDetalleClose}
        onCancel={handleCancel}
        usuarioId={user?.UsuarioID || user?.usuarioID}
      />
    );
  }

  return (
    <div className="content">
      <SnowFallComponent />
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <Row>
                <Col>
                  <CardTitle tag="h4">Ventas</CardTitle>
                </Col>
                <Col className="text-right">
                  <Button 
                    color="success" 
                    onClick={exportarVentasCSV} 
                    disabled={!ventas || ventas.length === 0}
                    className="mr-2"
                  >
                    <i className="tim-icons icon-cloud-download-93" /> Exportar CSV
                  </Button>
                  <Button color="primary" onClick={handleCreate} disabled={!sucursalSeleccionada}>
                    <i className="tim-icons icon-simple-add" /> Nueva Venta
                  </Button>
                </Col>
              </Row>
              <Row className="mt-3">
                <Col md="3">
                  <select
                    className="form-control"
                    value={sucursalSeleccionada}
                    onChange={(e) => setSucursalSeleccionada(e.target.value)}
                    disabled={loadingSucursales}
                  >
                    <option value="">Seleccione una sucursal</option>
                    {sucursales.map((suc) => (
                      <option key={suc.SucursalID} value={suc.SucursalID}>
                        {suc.Nombre}
                      </option>
                    ))}
                  </select>
                </Col>
                <Col md="2">
                  <Input
                    type="text"
                    placeholder="Cliente ID"
                    value={filtroCliente}
                    onChange={(e) => setFiltroCliente(e.target.value)}
                  />
                </Col>
                <Col md="2">
                  <Input
                    type="date"
                    placeholder="Fecha desde"
                    value={filtroFechaDesde}
                    onChange={(e) => setFiltroFechaDesde(e.target.value)}
                  />
                </Col>
                <Col md="2">
                  <Input
                    type="date"
                    placeholder="Fecha hasta"
                    value={filtroFechaHasta}
                    onChange={(e) => setFiltroFechaHasta(e.target.value)}
                  />
                </Col>
                <Col md="2">
                  <Input
                    type="select"
                    value={filtroEstatus}
                    onChange={(e) => setFiltroEstatus(e.target.value)}
                  >
                    <option value="">Todos</option>
                    <option value="Completada">Completada</option>
                    <option value="Cancelada">Cancelada</option>
                  </Input>
                </Col>
                <Col md="1">
                  <Button color="primary" onClick={loadVentas} disabled={!sucursalSeleccionada}>
                    <i className="tim-icons icon-zoom-split" />
                  </Button>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              {error && (
                <Alert color="danger" className="mb-3">
                  {error}
                </Alert>
              )}
              {success && (
                <Alert color="success" className="mb-3">
                  {success}
                </Alert>
              )}

              {loading ? (
                <Loading />
              ) : (
                <DataTable columns={columns} data={ventas} />
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      <ConfirmModal
        isOpen={showCancelModal}
        toggle={() => {
          setShowCancelModal(false);
          setVentaACancelar(null);
          setMotivoCancelacion('');
        }}
        title="Cancelar Venta"
        message={
          <div>
            <p>¿Está seguro de cancelar esta venta?</p>
            <Input
              type="textarea"
              placeholder="Motivo de cancelación (requerido)"
              value={motivoCancelacion}
              onChange={(e) => setMotivoCancelacion(e.target.value)}
              rows="3"
              className="mt-2"
            />
          </div>
        }
        confirmText="Cancelar Venta"
        cancelText="Cerrar"
        onConfirm={confirmCancel}
        loading={cancelando}
      />
    </div>
  );
}

export default Ventas;

